import { ValidationSchema, ValidationRule, BlockPayload } from "./types";
import { Rules } from "./rules";

export class ValidationSchemaBuilder<T> {
  private rules: ValidationRule<BlockPayload<T>>[] = [];

  static create<T>(): ValidationSchemaBuilder<T> {
    return new ValidationSchemaBuilder<T>();
  }

  addRule(rule: ValidationRule<BlockPayload<T>>): this {
    this.rules.push(rule);
    return this;
  }

  addRules(rules: ValidationRule<BlockPayload<T>>[]): this {
    this.rules.push(...rules);
    return this;
  }

  // Convenience methods for common patterns
  requireName(message?: string): this {
    return this.addRules(Rules.uniqueName(message));
  }

  requireSource(message?: string): this {
    return this.addRule(Rules.source(message));
  }

  requireSources(min?: number, message?: string): this {
    return this.addRules(Rules.sources(min, message));
  }

  requireUrl(message?: string): this {
    return this.addRules(Rules.url(message));
  }

  optionalUrl(message?: string): this {
    return this.addRules(Rules.optionalUrl(message));
  }

  requireJsonValue(field?: string, message?: string): this {
    return this.addRules(Rules.jsonValue(field, message));
  }

  requireRegexPattern(field?: string, message?: string): this {
    return this.addRules(Rules.regexPattern(field, message));
  }

  requireNumericValue(field?: string, min?: number, max?: number): this {
    return this.addRules(Rules.numericValue(field, min, max));
  }

  requireSliderConfig(): this {
    return this.addRules(Rules.sliderConfig());
  }

  requireDoubleSource(): this {
    return this.addRules(Rules.doubleSource());
  }

  // Source-aware validation methods removed - sources infrastructure kept for future use

  // Conditional validation
  when(
    condition: (config: T) => boolean,
    builder: (builder: ValidationSchemaBuilder<T>) => ValidationSchemaBuilder<T>,
  ): this {
    const conditionalBuilder = ValidationSchemaBuilder.create<T>();
    builder(conditionalBuilder);

    // Apply condition to all rules in the conditional builder
    const conditionalRules = conditionalBuilder.build().rules.map(rule => ({
      ...rule,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      validate: (value: any, fullPayload: BlockPayload<T>) => {
        if (!condition(fullPayload.configuration)) {
          return true;
        }
        return rule.validate(value, fullPayload);
      },
      dependencies: [...(rule.dependencies || [])],
    }));

    return this.addRules(conditionalRules);
  }

  build(): ValidationSchema<BlockPayload<T>> {
    return { rules: [...this.rules] };
  }
}

// Utility function for common block patterns
export const createBlockSchema = <T>() => ValidationSchemaBuilder.create<T>();

// Pre-built schemas for common block types
export const CommonSchemas = {
  // Basic block with just name
  basic: <T>() => createBlockSchema<T>().requireName(),

  // Block with single source
  singleSource: <T>() => createBlockSchema<T>().requireName().requireSource(),

  // Block with multiple sources
  multiSource: <T>(min = 2) => createBlockSchema<T>().requireName().requireSources(min),

  // Block with URL endpoint
  endpoint: <T>() => createBlockSchema<T>().requireName().requireUrl(),

  // Block with optional URL
  optionalEndpoint: <T>() => createBlockSchema<T>().requireName().optionalUrl(),

  // Block with JSON configuration
  jsonConfig: <T>(field = "value") => createBlockSchema<T>().requireName().requireJsonValue(field),

  // Block with regex pattern
  regex: <T>(field = "regex_pattern") =>
    createBlockSchema<T>().requireName().requireRegexPattern(field),

  // Block with numeric value
  numeric: <T>(field = "value", min?: number, max?: number) =>
    createBlockSchema<T>().requireName().requireNumericValue(field, min, max),

  // Block with comparison (two sources)
  comparison: <T>() => createBlockSchema<T>().requireName().requireDoubleSource(),

  // Source-aware schemas removed - sources infrastructure kept for future use
};
